import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { Odd } from 'types/Odd';
import { LiveEventOdd } from 'types/LiveEvent';
import {
  europeanHandicapPick,
  positionalSide,
  POSITIONAL_TEAM_MARKETS,
} from 'helpers/marketLines';
import {
  formatTranslatedOddLabelParts,
  translateOddText,
  translateStoredMarketName,
} from 'helpers/betI18n';
import { describeMarket } from 'helpers/marketDescriptions';

/**
 * A pick's name on the slip and on tickets. A 1 / X / 2 handicap pick is just
 * 1, X or 2 - its line goes after the market's name (see marketLineSuffix) -
 * instead of the feed's "+1 (Tie)".
 */
export const getOddLabel = (
  odd: Odd | LiveEventOdd,
  t: TFunction,
  marketName?: string | null
) => {
  const european = europeanHandicapPick(marketName, odd as Odd);
  if (european) return <span>{european.pick}</span>;
  const { name, handicap, header } = formatTranslatedOddLabelParts(odd, t);
  return (
    <>
      {name && <span>{name}</span>}
      {handicap && <span> {handicap}</span>}
      {header && <span> ({header})</span>}
    </>
  );
};

/** " (0:1)" after a 1 / X / 2 handicap's name; nothing for other markets. */
export const marketLineSuffix = (
  marketName: string | null | undefined,
  odd: Odd | LiveEventOdd
) => {
  const european = europeanHandicapPick(marketName, odd as Odd);
  return european ? ` ${european.line}` : '';
};

const text = (v: unknown) => (v == null ? '' : String(v).trim());
const SCORE = /^\d+-\d+$/;
/** Markets whose picks "1" / "2" are the home / away side ("To Win · 2"). */
const WINNER_MARKET =
  /result|winner|to win|1x2|money ?line|3-way|^full ?time$/i;
/** bwin's "who scores the half's next goal": the ticket stores which goal of
 *  the half it is as the handicap (for settlement), not a line - shown, the
 *  pick read "2 2". */
const NEXT_TEAM_MARKET = /^(1st|2nd) Half - Next Team to Score$/i;

export type PickText = {
  /** The market in the player's language, with its line: "Hendikep (0:1)". */
  market: string;
  /** The team the pick is about, when it is about one ("Liverpool"). */
  team: string | null;
  /** What was picked, without the team: "1. poluvreme: Da", "-0.5". */
  pick: ReactNode;
  /** A plain explanation of the market, when there is one. */
  description: string | null;
};

/**
 * Everything a player needs to read one pick without guessing: the market,
 * whose team it is about, what was picked. The feed marks a team pick with
 * header "1" / "2" ("Clean Sheet", Asian handicap), which the slip printed as
 * "(1)"; "To Score In Half" does not mark it at all, so two picks on the same
 * match read the same (client, 2026-10-23). The team comes from the header,
 * from the stored side (tickets: the API keeps it as the handicap), or from
 * the pick's place among its twins (slip: marketOdds).
 */
export function describePick(
  {
    betName,
    odd,
    home,
    away,
    marketOdds,
  }: {
    betName: string | null | undefined;
    odd: Odd | LiveEventOdd;
    home?: string | null;
    away?: string | null;
    /** All odds of the pick's market, when known (the slip, the offer). */
    marketOdds?: Odd[] | null;
  },
  t: TFunction
): PickText {
  const market = `${translateStoredMarketName(betName, t)}${marketLineSuffix(
    betName,
    odd
  )}`;
  const description = describeMarket(betName);
  const teamOf = (side: string) =>
    side === '1' ? text(home) || '1' : side === '2' ? text(away) || '2' : null;
  const name = text(odd.name);
  const header = text(odd.header);
  const handicap = text(odd.handicap);

  if (NEXT_TEAM_MARKET.test(text(betName))) {
    const side = name === '1' || name === '2' ? name : null;
    return {
      market,
      team: side ? teamOf(side) : null,
      pick: translateOddText(name, t),
      description,
    };
  }

  if (POSITIONAL_TEAM_MARKETS.has(text(betName).toLowerCase())) {
    const side =
      handicap === '1' || handicap === '2'
        ? handicap
        : marketOdds
          ? positionalSide(marketOdds, odd as Odd)
          : null;
    const pick = [name, header]
      .filter(Boolean)
      .map((part) => translateOddText(part, t))
      .join(': ');
    return { market, team: side ? teamOf(side) : null, pick, description };
  }

  const sided =
    (header === '1' || header === '2') &&
    (name || handicap) &&
    !europeanHandicapPick(betName, odd as Odd);
  if (sided) {
    const team = teamOf(header);
    // A correct score names the winner with it: "3-1 (Liverpool)".
    if (SCORE.test(name)) {
      return { market, team: null, pick: `${name} (${team})`, description };
    }
    const { name: n, handicap: h } = formatTranslatedOddLabelParts(odd, t);
    return {
      market,
      team,
      pick: [n, h].filter(Boolean).join(' '),
      description,
    };
  }

  // Some feeds name the winner instead of 1 / 2: live "Belarus", older
  // prematch "Result: Barcelona".
  const named = name.match(/^Result:\s*(.+)$/i);
  const winner = named
    ? named[1].trim()
    : WINNER_MARKET.test(text(betName)) &&
        (name === text(home) || name === text(away))
      ? name
      : null;
  if (winner && name && !header && !handicap) {
    const team = winner;
    const side = team === text(home) ? '1' : team === text(away) ? '2' : null;
    return side
      ? { market, team, pick: side, description }
      : { market, team: null, pick: team, description };
  }

  // A bare "2" on "Fulltime Result" / tennis "To Win" says which side only by
  // its number; name it ("Atletico Madrid · 2").
  if (
    (name === '1' || name === '2') &&
    !header &&
    !handicap &&
    WINNER_MARKET.test(text(betName))
  ) {
    return { market, team: teamOf(name), pick: name, description };
  }

  return {
    market,
    team: null,
    pick: getOddLabel(odd, t, betName),
    description,
  };
}
